// Core pagination utilities shared between PagePreview and fullscreen overlay
// Single source of truth for building blocks and computing page-break positions

export const PAGE_WIDTH = 794;   // px @ 96dpi
export const PAGE_HEIGHT = 1123; // px @ 96dpi

// Extract margins marker and body HTML from preview HTML
export function extractMarginsAndBody(html, fallbackMargins = null) {
  const wrapper = document.createElement('div');
  wrapper.innerHTML = html || '';
  const marker = wrapper.querySelector('[data-a4-margins]');
  let margins = null;
  if (marker) {
    try { margins = JSON.parse(marker.getAttribute('data-a4-margins')) || null; } catch(_) { margins = null; }
    marker.remove();
  }
  const resolvedMargins = margins || fallbackMargins || null;
  return { margins: resolvedMargins, bodyHTML: wrapper.innerHTML };
}

// Resolve A4 content margins from computed style
export function getMarginsFromCss(cs) {
  const top = parseFloat(cs.getPropertyValue('--a4-padding-top')) || parseFloat(cs.paddingTop) || 0;
  const right = parseFloat(cs.getPropertyValue('--a4-padding-right')) || parseFloat(cs.paddingRight) || 0;
  const bottom = parseFloat(cs.getPropertyValue('--a4-padding-bottom')) || parseFloat(cs.paddingBottom) || 0;
  const left = parseFloat(cs.getPropertyValue('--a4-padding-left')) || parseFloat(cs.paddingLeft) || 0;
  return { top, right, bottom, left };
}

// Create hidden measuring container with preview typography
export function setupMeasureContainer(margins) {
  const measure = document.createElement('div');
  measure.style.cssText = [
    'position: fixed', 'left: -99999px', 'top: 0', 'width: '+PAGE_WIDTH+'px', 'z-index: -1', 'opacity: 0'
  ].join(';');
  // Создаём «фиктивный» корень с тем же классом, что и в редакторе,
  // чтобы на измеритель применились все правила `.tiptap-editor .ProseMirror`
  const fakeRoot = document.createElement('div');
  fakeRoot.className = 'tiptap-editor';
  const content = document.createElement('div');
  content.className = 'a4-preview-content ProseMirror';
  content.style.cssText = [
    `width: ${PAGE_WIDTH}px`,
    `padding-left: ${(margins.left || 0)}px`,
    `padding-right: ${(margins.right || 0)}px`,
    `margin-top: ${(margins.top || 0)}px`,
    `margin-bottom: ${(margins.bottom || 0)}px`,
    'box-sizing: border-box',
    'white-space: pre-wrap',
    'display: flow-root'
  ].join(';');

  // Try to inherit key typography from live ProseMirror for exact metrics
  try {
    const editorEl = document.querySelector('.tiptap-editor .ProseMirror');
    if (editorEl) {
      const cs = getComputedStyle(editorEl);
      const copyProps = [
        'font-family','font-size','line-height','letter-spacing','word-spacing',
        'text-transform','font-kerning','font-variant-ligatures','text-rendering',
        'hyphens','word-break','overflow-wrap','tab-size','-moz-tab-size'
      ];
      copyProps.forEach(p => {
        const v = cs.getPropertyValue(p);
        if (v) content.style.setProperty(p, v);
      });
      const bw = cs.getPropertyValue('--table-border-width') || localStorage.getItem('aot_table_border_width') || '1px';
      content.style.setProperty('--table-border-width', String(bw).trim());
      const mh = cs.getPropertyValue('--table-row-min-height') || localStorage.getItem('aot_table_row_min_height') || '0px';
      content.style.setProperty('--table-row-min-height', String(mh).trim());
      const pad = cs.getPropertyValue('--table-cell-padding') || localStorage.getItem('aot_table_cell_padding') || '';
      if (pad) content.style.setProperty('--table-cell-padding', String(pad).trim());
    }
  } catch(_) {}

  fakeRoot.appendChild(content);
  measure.appendChild(fakeRoot);
  document.body.appendChild(measure);

  const contentHeight = PAGE_HEIGHT - (margins.top || 0) - (margins.bottom || 0);
  const cleanup = () => { try { document.body.removeChild(measure); } catch(_) {} };
  return { measure, content, contentHeight, cleanup };
}

const BLOCK_TAGS = ['P','DIV','H1','H2','H3','H4','H5','H6','UL','OL','LI','TABLE','PRE','BLOCKQUOTE'];
const isBlockTag = (tag) => BLOCK_TAGS.includes(tag);

// Build an ordered list of block-like nodes from a container
export function buildBlocksFromContainer(container) {
  const blocks = [];

  const pushNode = (n) => {
    if (!n) return;
    if (n.nodeType === Node.TEXT_NODE) {
      const t = (n.textContent || '').trim();
      if (t) {
        const p = document.createElement('p');
        p.textContent = t;
        blocks.push(p);
      }
      return;
    }
    if (n.nodeType === Node.ELEMENT_NODE) {
      const el = n;
      const dataType = el.getAttribute ? el.getAttribute('data-type') : null;
      if (dataType === 'page-spacer' || dataType === 'physical-page-break') {
        const spacer = document.createElement('div');
        spacer.setAttribute('data-type', dataType);
        blocks.push(spacer);
        return;
      }
      const tag = el.tagName;
      if (isBlockTag(tag)) {
        if (tag === 'DIV' && el.childNodes && el.childNodes.length > 0) {
          Array.from(el.childNodes).forEach(pushNode);
        } else if (tag === 'P') {
          // Preserve empty paragraphs as measurable blocks
          const textContent = el.textContent || '';
          const hasOnlyBr = el.childNodes.length === 1 && el.firstChild && el.firstChild.tagName === 'BR';
          const isEmpty = textContent.trim() === '';
          if (isEmpty || hasOnlyBr) {
            const p = document.createElement('p');
            p.setAttribute('data-empty', 'true');
            if (hasOnlyBr) p.appendChild(document.createElement('br'));
            blocks.push(p);
          } else {
            blocks.push(el.cloneNode(true));
          }
        } else {
          blocks.push(el.cloneNode(true));
        }
        return;
      }
      // Wrap inline/unknown elements into a paragraph
      const p = document.createElement('p');
      p.innerHTML = el.outerHTML || el.textContent || '';
      blocks.push(p);
      return;
    }
  };

  Array.from(container.childNodes).forEach((node) => {
    // Skip service overlays if any
    if (node && node.nodeType === Node.ELEMENT_NODE) {
      const el = node;
      if (el.classList && (el.classList.contains('a4-breaks-overlay') || el.classList.contains('a4-pages-overlay') || el.classList.contains('page-break-line'))) {
        return;
      }
    }
    pushNode(node);
  });
  return blocks;
}

// Split a simple paragraph by words to fit available height
export function splitParagraphToFit(node, contentEl, fitsFn) {
  const text = (node.textContent || '').split(/\s+/);
  const left = document.createElement('p');
  let i = 0;
  while (i < text.length) {
    const candidate = (left.textContent ? left.textContent + ' ' : '') + text[i];
    left.textContent = candidate;
    contentEl.appendChild(left);
    if (!fitsFn()) {
      left.textContent = (left.textContent || '').replace(/\s*\S+$/, '').trim();
      contentEl.removeChild(left);
      contentEl.appendChild(left);
      break;
    }
    i++;
  }
  const right = document.createElement('p');
  right.textContent = text.slice(i).join(' ');
  return { left, right };
}

// Smart splitter that respects inline markup inside a paragraph
// Tries to move children one by one; for text nodes splits by words
export function splitRichParagraphToFit(node, contentEl, fitsFn) {
  const makeEmptyParagraphClone = (src) => {
    const p = document.createElement('p');
    // Copy inline attrs that might affect layout (class/style)
    try {
      if (src.getAttribute('class')) p.setAttribute('class', src.getAttribute('class'));
      if (src.getAttribute('style')) p.setAttribute('style', src.getAttribute('style'));
      if (src.getAttribute('align')) p.setAttribute('align', src.getAttribute('align'));
    } catch(_) {}
    return p;
  };
  const left = makeEmptyParagraphClone(node);
  const right = makeEmptyParagraphClone(node);

  const appendWords = (textNode, target) => {
    const words = (textNode.textContent || '').split(/\s+/);
    const leftText = document.createTextNode('');
    target.appendChild(leftText);
    let i = 0;
    while (i < words.length) {
      const candidate = (leftText.textContent ? leftText.textContent + ' ' : '') + words[i];
      leftText.textContent = candidate;
      if (!fitsFn()) {
        leftText.textContent = (leftText.textContent || '').replace(/\s*\S+$/, '').trim();
        break;
      }
      i++;
    }
    const restText = words.slice(i).join(' ');
    return restText ? document.createTextNode(restText) : null;
  };

  const appendNodeSmart = (n, target) => {
    if (n.nodeType === Node.TEXT_NODE) {
      return appendWords(n, target);
    }
    if (n.nodeType === Node.ELEMENT_NODE) {
      const clone = n.cloneNode(false);
      target.appendChild(clone);
      const children = Array.from(n.childNodes);
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const rest = appendNodeSmart(child, clone);
        if (rest) {
          // Не поместилось — остаток помещаем в правую часть
          const rightClone = n.cloneNode(false);
          if (rest.nodeType) rightClone.appendChild(rest);
          for (let j = i + 1; j < children.length; j++) {
            rightClone.appendChild(children[j].cloneNode(true));
          }
          return rightClone; // вернуть хвост как единый элемент
        }
      }
      // Всё дитя вошли; проверим fit ещё раз
      if (!fitsFn()) {
        // если даже пустой эл-т не влазит — отдаём целиком в правую
        target.removeChild(clone);
        return n.cloneNode(true);
      }
      return null;
    }
    return null;
  };

  // Переносим содержимое node в left, пока влазит, остаток — в right
  const children = Array.from(node.childNodes);
  for (let i = 0; i < children.length; i++) {
    const tail = appendNodeSmart(children[i], left);
    if (tail) {
      right.appendChild(tail);
      for (let j = i + 1; j < children.length; j++) {
        right.appendChild(children[j].cloneNode(true));
      }
      break;
    }
  }
  // Если right пуст — значит всё поместилось
  if (!right.firstChild) return null;
  return { left, right };
}

// Try to split a table by rows; keep the header if present
export function splitTableByRows(tableNode, contentEl, fitsFn) {
  const src = tableNode.cloneNode(true);
  const thead = src.querySelector('thead');
  const body = src.querySelector('tbody');
  const rows = Array.from((body || src).querySelectorAll('tr'));

  const shell = document.createElement('table');
  if (thead) shell.appendChild(thead.cloneNode(true));
  const shellBody = document.createElement('tbody');
  shell.appendChild(shellBody);

  let i = 0;
  while (i < rows.length) {
    shellBody.appendChild(rows[i].cloneNode(true));
    contentEl.appendChild(shell);
    if (!fitsFn()) {
      shellBody.removeChild(shellBody.lastChild);
      contentEl.removeChild(shell);
      contentEl.appendChild(shell);
      break;
    }
    i++;
  }

  if (i >= rows.length) return null; // everything fit

  const rest = document.createElement('table');
  if (thead) rest.appendChild(thead.cloneNode(true));
  const restBody = document.createElement('tbody');
  rest.appendChild(restBody);
  while (i < rows.length) {
    restBody.appendChild(rows[i++].cloneNode(true));
  }
  return rest;
}

// Compute absolute break Y positions from preview HTML (with margins marker)
export function computeBreaksFromHtml(html) {
  try {
    const { margins: htmlMargins, bodyHTML } = extractMarginsAndBody(html || '');
    const margins = htmlMargins || { top: 0, right: 0, bottom: 0, left: 0 };

    const { content, contentHeight, cleanup } = setupMeasureContainer(margins);

    const parser = document.createElement('div');
    parser.innerHTML = bodyHTML || '';
    const blocks = buildBlocksFromContainer(parser);

    const breaks = [];
    let consumedY = 0; // relative to content top
    const fits = () => content.scrollHeight <= contentHeight;
    const newPage = () => {
      breaks.push(consumedY + contentHeight);
      content.innerHTML = '';
      consumedY += contentHeight;
    };

    for (let i = 0; i < blocks.length; i++) {
      const node = blocks[i];
      if (node.getAttribute && node.getAttribute('data-type') === 'page-spacer') { newPage(); continue; }
      const clone = node.cloneNode(true);
      content.appendChild(clone);
      if (fits()) continue;
      // overflow handling
      content.removeChild(clone);
      if (clone.tagName === 'P') {
        const res = clone.childElementCount === 0
          ? splitParagraphToFit(clone, content, fits)
          : splitRichParagraphToFit(clone, content, fits);
        content.appendChild(res.left);
        if (!fits()) { content.removeChild(res.left); newPage(); content.appendChild(res.left); }
        if (res.right.textContent && res.right.textContent.trim()) { newPage(); content.appendChild(res.right); }
      } else if (clone.tagName === 'TABLE') {
        const rest = splitTableByRows(clone, content, fits);
        if (rest) { newPage(); content.appendChild(rest); } else { newPage(); content.appendChild(clone); }
      } else {
        newPage();
        content.appendChild(clone);
      }
    }

    cleanup();
    const adjusted = breaks.map(y => (margins.top || 0) + y);
    // Normalize: keep exact grid but remove duplicates/nearby
    const sorted = adjusted.sort((a,b)=>a-b);
    const out = [];
    const MIN_SEP = 8;
    for (const y of sorted) {
      if (!out.length || Math.abs(y - out[out.length-1]) >= MIN_SEP) out.push(Math.round(y));
    }
    return out;
  } catch(_) {
    return [];
  }
}
